import { Typography, Grid, CardMedia, Button, IconButton, SelectChangeEvent } from '@mui/material';
import { Formik, FormikHelpers, useField, validateYupSchema, yupToFormErrors } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateApplication } from '../../redux-store/actions/application';
import {
  individuals_Poa_nonIndividuals_Documents,
  FileType,
  Bank,
  KraDocumentType,
  ApplicationProps,
} from '../../redux-store/types/api-types';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { RootStateType } from '../../redux-store/reducers';
import { Box } from '@mui/system';
import {
  ENABLE_KRA_DOCUMENT_FETCH,
  IMAGE_UPLOAD_FORMATS,
  RiskProfileEnabled,
  USER_ROLES,
  SAVE_LATER_VALIDATION_CHECK,
  DOCUMENT_ALLOW_PASTE,
  docInvalidCheck,
} from '../../utils/constant';
import { Notes, ProceedSaveLater, SubHeading2 } from '../investors/components';
import { useHistory } from 'react-router';
import {
  NonIndividualApplicantDocuments,
  applicationComparison,
  checkIfApplicationIsNonIndividualPOA,
  fatcaDocumentCheck,
  getDocNumberForMultipleDocs,
  getStep,
  isBankAccountProof,
  isDocTypeCheque,
  nonIndividualTopupDocuments,
  saveForLater,
  statusRoleBased,
} from '../../utils/utilityFunctions';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { nonIndividualDocumentDetailsSchema } from '../../utils/schema';
import { logout, showError } from '../../redux-store/actions/auth';
import MFSelectField from '../../lib/formik/SelectField';
import { useSnackbar } from 'notistack';
import { KraDocument } from '../investors/kraDocument';
import { FileUploadDialog, MultipleInfoDialog } from '../commonComponents';
import en from '../../lang/en-us';
import { useMdmsDocumentData, useMdmsTopUpDocumentData } from '../../utils/useDataMdms';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MFCheckbox from '../../lib/formik/Checkbox';
import { DatePicker } from '../../lib/formik/DatePicker';

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginFileValidateSize,
  FilePondPluginFileValidateType
);

type documentDetails = {
  documentType: string;
  documentName: string;
  documentId: string;
  isActive: boolean;
  isVisible: boolean;
  file?: FileType;
  chequeIssuingDate?: string | null;
  required?: string;
  uniqueKey?: string;
  options?: string[];
  infoText?: string[];
  errorOnUpload?: boolean;
  [key: string]: string | number | boolean | FileType | undefined | string[] | null;
};

export type documentProps = {
  documents: {
    documentType: string;
    documentName: string;
    documentsList: documentDetails[];
    multipleFiles: string;
    required: string;
    infoText?: string[];
    options?: string[];
    downloadTemplate?: string;
  }[];
  kraDocument?: KraDocumentType[];
  wishToUploadFatcaDocument?: boolean | null;
};
export interface Values {
  topUpApplicants: documentProps[];
  applicants: documentProps[];
  saveType: string;
  banks: Bank[];
  isTopup: boolean;
  [key: string]: documentProps[] | string | undefined | string[] | Bank[] | boolean;
}

export const FileUpload = ({
  onFileChange,
  name,
  documentData,
  disabled = false,
}: {
  onFileChange: (
    value: string,
    documentId: string,
    errorOnUpload?: boolean,
    removeFile?: boolean
  ) => void;
  name: string;
  documentData: documentDetails;
  disabled?: boolean;
}): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [files, setFiles] = useState<any>([]);
  const history = useHistory();
  const authToken = useSelector((store: RootStateType) => store.auth.token);
  const dispatch = useDispatch();
  const [open, setOpen] = useState({ show: false, message: '', code: '', documentName: '' });

  // eslint-disable-next-line
  const handleOpen = (result: any) => {
    setOpen({
      show: true,
      message: result.message,
      code: result.code,
      documentName: result.documentName,
    });
  };
  const handleClose = () => {
    setOpen({ show: false, message: '', code: '', documentName: '' });
  };
  useEffect(() => {
    if (files?.length === 0) {
      onFileChange('', documentData?.documentId || '', false);
    }
  }, [files]);
  const handleFileChange = (name: string, documentId: string) => {
    try {
      onFileChange(name, documentId);
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  return (
    <>
      {!documentData.file ? (
        <Box
          sx={{
            '.filepond--credits': { display: 'none' },
            '.filepond--label-action': {
              textDecoration: 'none',
              fontSize: '14px',
            },
            '.filepond--drop-label': {
              cursor: disabled ? 'not-allowed' : 'pointer',
              pointerEvents: 'all',
            },
            '.filepond--file': {
              color: '#000',
              flexWrap: 'wrap',
              fontWeight: 500,
            },
          }}>
          <FilePond
            files={files}
            server={{
              process: {
                url: '/api/files/files',
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
                onload: (response): string => {
                  const result = JSON.parse(response);
                  result?.name && result?.id && handleFileChange(result.name, result.id);
                  return response;
                },
                onerror: (response) => {
                  if (response) {
                    const result = JSON.parse(response);
                    if (result && result.message) {
                      onFileChange('', '', true, true);
                      handleOpen({ ...result, documentName: documentData.documentName });
                    }
                  } else {
                    onFileChange('', '', true, true);
                    handleOpen({
                      code: '',
                      message: en.networkText.unableToProcess,
                      documentName: documentData.documentName,
                    });
                  }
                  return response;
                },
              },
            }}
            onupdatefiles={(fileItems) => {
              setFiles(fileItems);
            }}
            // onremovefile={() => {
            //   onFileChange('', '',false, true);
            // }}
            // eslint-disable-next-line
            onaddfile={(error, file) => {
              if (error) {
                onFileChange('', '', true, true);
              }
            }}
            // eslint-disable-next-line
            onprocessfile={(error, file) => {
              if (error) {
                if (error.code === 401) {
                  dispatch(logout());
                  // history.push('/login');
                }
              }
            }}
            onprocessfilerevert={() => {
              onFileChange('', '', false, true);
            }}
            name="file"
            maxFileSize={'2MB'}
            allowMultiple={false}
            maxFiles={1}
            acceptedFileTypes={IMAGE_UPLOAD_FORMATS}
            labelIdle='<span class="filepond--label-action">Choose File <div>Max Size: 2MB</div>Supports: jpeg, pdf, png, jpg</span>'
            labelMaxFileSize={'Max file size is 2 MB'}
            labelFileTypeNotAllowed={'Invalid file type'}
            fileValidateTypeLabelExpectedTypesMap={{
              'image/jpeg': '.jpeg',
              'image/jpg': '.jpg',
              'image/png': '.png',
              'application/pdf': '.pdf',
            }}
            imagePreviewHeight={100}
            allowPaste={DOCUMENT_ALLOW_PASTE}
            disabled={disabled}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: '1px dashed #AAB2BA',
            borderRadius: '6px',
            mb: 2,
            minHeight: '65px',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CardMedia
              component="img"
              src={documentData.file.thumbnailUrl || '/images/photo-placeholder.jpg'}
              alt="aadhaar"
              width="50"
              height="50"
              sx={{ display: 'flex', width: 'unset', mx: 1 }}
            />
          </Box>
          <Typography
            component="span"
            sx={{ whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
            {documentData.file.name}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              '.MuiButton-root': { fontWeight: 500, fontSize: 14, p: 0, mr: 1 },
              flexWrap: 'wrap',
            }}>
            <Button
              sx={{
                color: 'primary.main',
                border: '1px dashed #2057A6',
                mb: 1,
              }}
              onClick={() => window.open(documentData.file?.viewUrl)}>
              View
            </Button>
            {!disabled && (
              <Button
                variant="contained"
                onClick={() => {
                  onFileChange('', '', false, true);
                }}
                sx={{ mb: 1 }}>
                Replace
              </Button>
            )}
            <IconButton
              sx={{
                p: 0,
                pr: 0.5,
                ':hover': {
                  bgcolor: '#F4FCFC',
                },
                cursor: 'pointer',
                pointerEvents: 'all',
                mb: 1,
              }}
              onClick={() => window.open(documentData.file?.url, '_blank')}>
              <CardMedia
                component="img"
                src={'/images/download-icon.png'}
                alt="Download"
                sx={{ width: '25px' }}
              />
            </IconButton>
          </Box>
        </Box>
      )}
      <FileUploadDialog
        handleClose={handleClose}
        show={open.show}
        message={open.message}
        code={open.code}
        documentName={open.documentName}
      />
      {!!errorText && (
        <Typography
          sx={{
            color: '#d32f2f',
            fontSize: '0.75rem',
          }}>
          {errorText}
        </Typography>
      )}
    </>
  );
};

export const document_object = {
  documentId: '',
  isActive: true,
  errorOnUpload: false,
};

export const ApplicantTopUpDocuments = ({
  values,
  getLabel,
  setFieldValue,
  handleOpen,
  setPopUpText,
}: {
  values: Values;
  getLabel: (documentName: string, required: string) => string;
  setFieldValue: FormikHelpers<Values>['setFieldValue'];
  handleOpen: () => void;
  setPopUpText: (e: { popUpInfoText: string[] } | null) => void;
}): JSX.Element => {
  const { application } = useSelector((store: RootStateType) => store.application);
  const dispatch = useDispatch();
  return application?.isTopup ? (
    <>
      <SubHeading2>Top Up Documents</SubHeading2>
      {values.topUpApplicants.map((applicant, ind) => {
        const { documents } = applicant;
        return (
          <>
            {documents.map((document, doc_index) => {
              const {
                documentType,
                documentName,
                documentsList = [],
                multipleFiles,
                options,
                required,
                infoText,
              } = document;
              return (
                <>
                  <React.Fragment key={ind}>
                    <Grid item xs={12} sm={6} display="flex" alignItems="baseline">
                      <Typography
                        component="span"
                        sx={{ color: '#2057A6', fontSize: '20px', paddingRight: '15px' }}>
                        {doc_index + 1}
                      </Typography>
                      {!options && !infoText && (
                        <Typography
                          component="span"
                          sx={{ fontSize: 14, fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}>
                          {documentName} {required === 'true' ? '*' : ''}
                        </Typography>
                      )}
                      {!options && infoText && (
                        <Typography
                          sx={{ fontSize: 14, fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}>
                          {documentName} {required === 'true' ? '*' : ''}
                          <Typography component="span" pl={0.5} sx={{ cursor: 'pointer' }}>
                            <InfoOutlinedIcon
                              color="info"
                              fontSize="small"
                              onClick={() => {
                                setPopUpText({ popUpInfoText: infoText });
                                handleOpen();
                              }}
                            />
                          </Typography>
                        </Typography>
                      )}
                      {options &&
                        documentsList
                          .filter(
                            (document, index) =>
                              documentsList.findIndex(
                                (obj) => document.documentType === obj.documentType
                              ) === index
                          )
                          .map((doc, index) => (
                            <React.Fragment key={index}>
                              <Grid xs={12} sm={12} sx={{ position: 'relative', bottom: '15px' }}>
                                <MFSelectField
                                  name={`topUpApplicants.${ind}.documents.${doc_index}.documentsList.${index}.documentName`}
                                  label={getLabel(documentName, required)}
                                  items={options.map((item: string) => ({
                                    key: item,
                                    value: item,
                                  }))}
                                  applyLabelStyles={true}
                                  onChange={({ target: { value } }: SelectChangeEvent<unknown>) => {
                                    const updatedDocumentList = values.topUpApplicants[
                                      ind
                                    ].documents[doc_index].documentsList.map((docList) => {
                                      if (docList.documentType === doc.documentType) {
                                        return {
                                          ...docList,
                                          documentName: value,
                                        };
                                      }
                                      return docList;
                                    });
                                    setFieldValue(
                                      `topUpApplicants.${ind}.documents.${doc_index}.documentsList`,
                                      updatedDocumentList
                                    );
                                  }}
                                  infoText={infoText || []}
                                />
                              </Grid>
                            </React.Fragment>
                          ))}
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      display="flex"
                      flexDirection="column"
                      mt={options ? { xs: 1, sm: 3 } : 0}>
                      {documentsList
                        .filter((item) => item.isVisible)
                        .map((doc, index) => (
                          <React.Fragment key={doc.uniqueKey}>
                            <FileUpload
                              onFileChange={(value, documentId, errorOnUpload, removeFile) => {
                                const { file, ...rest } =
                                  values.topUpApplicants[ind].documents[doc_index].documentsList[
                                    Number(doc?.uniqueKey?.split('-')[2])
                                  ];
                                setFieldValue(
                                  `topUpApplicants.${ind}.documents.${doc_index}.documentsList.${
                                    doc?.uniqueKey?.split('-')[2]
                                  }`,
                                  {
                                    ...rest,
                                    documentId,
                                    ...(!removeFile && { file }),
                                    errorOnUpload: errorOnUpload || document_object.errorOnUpload,
                                  }
                                );
                              }}
                              name={`topUpApplicants.${ind}.documents.${doc_index}.documentsList.${
                                doc?.uniqueKey?.split('-')[2]
                              }.documentId`}
                              documentData={doc}
                            />
                            {index > 0 && doc.isVisible && !doc.documentId && (
                              <DeleteOutlineIcon
                                onClick={() => {
                                  const getDocs = values.topUpApplicants[ind].documents[
                                    doc_index
                                  ].documentsList.map((_item, i) => ({
                                    ..._item,
                                    isVisible:
                                      i !== Number(doc?.uniqueKey?.split('-')[2])
                                        ? _item.isVisible
                                        : false,
                                  }));
                                  // .filter((_, i) => i !== index);
                                  setFieldValue(
                                    `topUpApplicants.${ind}.documents.${doc_index}.documentsList`,
                                    getDocs
                                  );
                                }}
                                color="error"
                                sx={{
                                  alignSelf: 'end',
                                  position: 'relative',
                                  bottom: '90px',
                                  left: '22px',
                                  cursor: 'pointer',
                                }}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      {multipleFiles === 'true' &&
                        documentsList.filter((item) => item.isVisible).length <
                          (documentType === 'additional_document_topup' ? 3 : 2) && (
                          <Typography
                            onClick={() => {
                              const currentDocTypeListLength =
                                values.topUpApplicants[ind].documents[doc_index].documentsList
                                  .length;
                              const {
                                required = true,
                                documentType,
                                documentName,
                                // eslint-disable-next-line
                                documentId,
                              } = values.topUpApplicants[ind].documents[doc_index].documentsList[
                                currentDocTypeListLength - 1
                              ];
                              try {
                                documentsList
                                  .filter((item) => item.isVisible)
                                  .forEach((item, _i) => {
                                    if (!item.documentId) {
                                      throw Error(
                                        `Please upload the ${getDocNumberForMultipleDocs(
                                          _i + 1
                                        )}document to add more documents`
                                      );
                                    }
                                  });
                                setFieldValue(
                                  `topUpApplicants.${ind}.documents.${doc_index}.documentsList`,
                                  [
                                    ...values.topUpApplicants[ind].documents[doc_index]
                                      .documentsList,
                                    {
                                      ...document_object,
                                      uniqueKey:
                                        ind.toString() +
                                        '-' +
                                        doc_index.toString() +
                                        '-' +
                                        currentDocTypeListLength.toString(),
                                      isVisible: true,
                                      documentType,
                                      documentName,
                                      required,
                                    },
                                  ]
                                );
                              } catch (e) {
                                dispatch(showError((e as Error).message));
                              }
                            }}
                            sx={{
                              color: '#417850',
                              fontSize: '12px',
                              fontWeight: 500,
                              textDecoration: 'underline',
                              alignSelf: 'end',
                              cursor: 'pointer',
                            }}>
                            {'Add another document'}
                          </Typography>
                        )}
                    </Grid>
                  </React.Fragment>
                </>
              );
            })}
          </>
        );
      })}
      {!application?.isPhysicalApplication && (
        <SubHeading2>Existing Application Documents</SubHeading2>
      )}
    </>
  ) : (
    <></>
  );
};

export default function DocumentDetails(): JSX.Element {
  const [documentsData, setDocumentsData] = useState<individuals_Poa_nonIndividuals_Documents[]>(
    []
  );
  const [documentsDataTopUp, setDocumentsDataTopUp] = useState<
    individuals_Poa_nonIndividuals_Documents[]
  >([]);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const { application } = useSelector((store: RootStateType) => store.application);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const { enqueueSnackbar } = useSnackbar();
  const { Document } = useMdmsDocumentData();
  const { TopUpDocument } = useMdmsTopUpDocumentData();

  useEffect(() => {
    (async function () {
      try {
        const { applicants = [] } = application || {};
        const getInvestorType = applicants[0]?.investorType?.toString();
        if (getInvestorType) {
          if (getInvestorType && Object.keys(Document).includes(getInvestorType)) {
            if (checkIfApplicationIsNonIndividualPOA(application as ApplicationProps)) {
              setDocumentsData(
                Document[`${getInvestorType}_poa`] as individuals_Poa_nonIndividuals_Documents[]
              );
              setDocumentsDataTopUp(
                TopUpDocument[
                  `${getInvestorType}_poa`
                ] as individuals_Poa_nonIndividuals_Documents[]
              );
            } else {
              setDocumentsData(
                Document[getInvestorType] as individuals_Poa_nonIndividuals_Documents[]
              );
              setDocumentsDataTopUp(
                TopUpDocument[getInvestorType] as individuals_Poa_nonIndividuals_Documents[]
              );
            }
          }
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, [application, Document, TopUpDocument]);

  const { applicants = [], banks = [] } = application || {};

  const initialValues: Values = {
    topUpApplicants: nonIndividualTopupDocuments(application, documentsDataTopUp),
    applicants: NonIndividualApplicantDocuments(application, documentsData, role),
    saveType: 'save and proceed',
    banks: banks,
    isTopup: !!application?.isTopup,
  };
  const handleSubmit = async (values: Values) => {
    const { applicants, saveType, topUpApplicants } = values;
    const {
      applicants: exisitingApplicants = [],
      id,
      applicant1ReferenceId = '',
      currentStep,
      applicationNumber,
      topupApplicationNumber,
      isTopup = false,
    } = application || {};
    const updatedApplicants = exisitingApplicants.map((applicant, index) => {
      const { documents = [] } = applicants[index] || {};
      const { documents: topUpDocuments = [] } = topUpApplicants[index] || {};
      const updatedDoc = documents.map((doc) => {
        return {
          ...doc,
          documentsList: doc.documentsList.map((_doc) => {
            return { ..._doc, ...(isTopup ? { isTopup: false } : {}) };
          }),
        };
      });
      const finalDocs = (
        isTopup
          ? updatedDoc.concat(
              topUpDocuments.map((doc) => {
                return {
                  ...doc,
                  documentsList: doc.documentsList.map((_doc) => {
                    return { ..._doc, isTopup: true };
                  }),
                };
              })
            )
          : updatedDoc
      )
        .map((doc) => {
          if (doc.documentsList.some((_documentCheck) => _documentCheck?.errorOnUpload)) {
            throw docInvalidCheck;
          }
          return doc.documentsList.filter((d) => d.documentId);
        })
        .concat([
          ...((
            applicant?.documents?.filter(
              (doc) =>
                [
                  'tin_declaration_1',
                  'tin_declaration_2',
                  'tin_declaration_3',
                  'tin_declaration_4',
                ].includes(doc?.documentType) && doc?.documentId
            ) || []
          ).map((doc) => ({ ...doc, isVisible: true })) as unknown as documentDetails[][]),
        ]);
      return {
        ...applicant,
        documents: finalDocs.flat().map((doc) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { uniqueKey, required, documentName, isVisible, infoText, errorOnUpload, ...rest } =
            doc;
          if (!isDocTypeCheque(documentName)) {
            return { ...rest, documentName, chequeIssuingDate: null };
          }
          return { ...rest, documentName };
        }),
        wishToUploadFatcaDocument: applicants[index].wishToUploadFatcaDocument,
      };
    });
    const checkApplication = applicationComparison(
      {
        ...application,
        applicants: application?.applicants
          ?.map((applicant) => {
            return {
              ...applicant,
              documents: applicant.documents
                ?.map((document: any) => {
                  const {
                    documentData,
                    // id,
                    // applicantId,
                    filename,
                    updatedAt,
                    createdAt,
                    updatedBy,
                    createdBy,
                    ...rest
                  } = document;
                  return { ...rest };
                })
                .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId)),
            };
          })
          .sort((applicant1, applicant2) => Number(applicant1.id) - Number(applicant2.id)),
      },
      {
        ...application,
        applicants: updatedApplicants
          .map((applicant) => {
            return {
              ...applicant,
              documents: applicant.documents
                ?.map((document: any) => {
                  // eslint-disable-next-line
                  const { documentData, options, downloadTemplate, infoText, ...rest } = document;
                  return { ...rest };
                })
                .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId)),
            };
          })
          .sort((applicant1, applicant2) => Number(applicant1.id) - Number(applicant2.id)),
        currentStep:
          !!currentStep && currentStep > (application?.isTopup ? 4 : RiskProfileEnabled ? 6 : 5)
            ? currentStep
            : Number(currentStep) + 1,
      }
    );
    const isSaveLater = saveType !== 'save and proceed';
    try {
      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              applicants: updatedApplicants,
              currentStep: application?.isTopup
                ? 5
                : RiskProfileEnabled
                ? getStep(7, isSaveLater)
                : getStep(6, isSaveLater),
              status: statusRoleBased(role, application),
            },
            applicationId: id,
            ...(saveType !== 'save and proceed' && {
              toastMessage: '',
            }),
          })
        );
        saveType === 'save and proceed'
          ? application?.isTopup
            ? history.push(
                [USER_ROLES.AMCAPPROVER, USER_ROLES.POAAPPROVER].includes(role)
                  ? `/application-details/${id}`
                  : `/non-individual-application-preview/${id}`,
                { topUpApplication: application?.isTopup }
              )
            : history.push('authorised-signatory', {
                id,
                applicant1ReferenceId,
                topUpApplication: application?.isTopup,
              })
          : history.push(saveForLater(role, id, applicant1ReferenceId, application?.isTopup), {
              topUpApplication: application?.isTopup,
            });
      } else if (checkApplication) {
        if (saveType !== 'save and proceed') {
          enqueueSnackbar(
            `Application ${application?.isTopup ? topupApplicationNumber : applicationNumber} - ` +
              ' Saved successfully',
            {
              variant: 'success',
              autoHideDuration: 3000,
            }
          );
        }
        saveType === 'save and proceed'
          ? application?.isTopup
            ? history.push(
                [USER_ROLES.AMCAPPROVER, USER_ROLES.POAAPPROVER].includes(role)
                  ? `/application-details/${id}`
                  : `/non-individual-application-preview/${id}`,
                { topUpApplication: application?.isTopup }
              )
            : history.push('authorised-signatory', {
                id,
                applicant1ReferenceId,
                topUpApplication: application?.isTopup,
              })
          : history.push(saveForLater(role, id, applicant1ReferenceId, application?.isTopup), {
              topUpApplication: application?.isTopup,
            });
      }
    } catch (e) {
      setLoading(false);
      console.error((e as Error).message);
    }
  };

  const getLabel = (documentName: string, required: string): string => {
    return `${documentName}  ${required === 'true' ? '*' : ''} `;
  };

  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState<{ popUpInfoText: string[] } | null>(null);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      validate={(values: Values) => {
        try {
          validateYupSchema(values, nonIndividualDocumentDetailsSchema, true, values);
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}>
      {({ handleSubmit, values, setValues, setFieldValue }) => (
        <Grid
          container
          rowSpacing={2}
          columnSpacing={5}
          component="form"
          noValidate
          onSubmit={handleSubmit}
          display="flex"
          alignItems="flex-start">
          <Grid sx={{ ml: '40px', width: '100%' }}>
            <Notes
              displayContent={'Please upload latest documents not more than 2 months old'}
              displayContent1={"Please don't upload any password protected files"}
            />
          </Grid>
          <ApplicantTopUpDocuments
            getLabel={getLabel}
            setFieldValue={setFieldValue}
            values={values}
            handleOpen={handleOpen}
            setPopUpText={(e: { popUpInfoText: string[] } | null) => setPopUpText(e)}
          />
          {!application?.isPhysicalApplication &&
            values.applicants.map((applicant, ind) => {
              const { documents } = applicant;
              let num = 0;
              return (
                <>
                  <Grid item xs={12}>
                    <MFCheckbox
                      name={`applicants.${ind}.wishToUploadFatcaDocument`}
                      label={'Wish to upload FATCA-CRS Declaration'}
                      sx={{ my: 2 }}
                      onChange={({ target: { checked } }) => {
                        const findExistingFatca = documents.find(
                          (fatcaDoc) => fatcaDoc.documentType === 'fatca'
                        );
                        const getFatcaFromMasterJson = documentsData.find(
                          (_fatcaDoc) => _fatcaDoc.documentType === 'fatca'
                        );
                        const updatedDoc = findExistingFatca
                          ? documents
                              .filter((document) => {
                                if (
                                  fatcaDocumentCheck(!checked as boolean, document.documentType)
                                ) {
                                  return;
                                }
                                return document;
                              })
                              .map((_doc) => {
                                if (fatcaDocumentCheck(checked as boolean, _doc.documentType)) {
                                  return { ..._doc, required: 'true' };
                                }
                                return _doc;
                              })
                          : [
                              ...documents,
                              checked && getFatcaFromMasterJson
                                ? {
                                    documentType: getFatcaFromMasterJson.documentType,
                                    documentName: getFatcaFromMasterJson.documentName,
                                    documentsList: [
                                      {
                                        documentType: getFatcaFromMasterJson.documentType,
                                        documentName: getFatcaFromMasterJson.documentName,
                                        required: 'true',
                                        options: getFatcaFromMasterJson.options,
                                        downloadTemplate: getFatcaFromMasterJson.downloadTemplate,
                                        isVisible: true,
                                        uniqueKey: (ind.toString() +
                                          '-' +
                                          documents.length.toString() +
                                          '-' +
                                          '0') as string,
                                        ...document_object,
                                      },
                                    ],
                                    required: 'true',
                                    multipleFiles: getFatcaFromMasterJson.multipleFiles,
                                    options: getFatcaFromMasterJson.options,
                                    downloadTemplate: getFatcaFromMasterJson.downloadTemplate,
                                  }
                                : {},
                            ];
                        setFieldValue(`applicants.${ind}`, {
                          ...applicants[ind],
                          documents: updatedDoc,
                          wishToUploadFatcaDocument: checked,
                        });
                      }}
                    />
                  </Grid>
                  {documents.map((document, doc_index) => {
                    num = doc_index + 1;
                    const {
                      documentType,
                      documentName,
                      documentsList = [],
                      multipleFiles,
                      options,
                      required,
                      downloadTemplate,
                      infoText,
                    } = document;
                    return (
                      <>
                        <React.Fragment key={ind}>
                          <Grid item xs={12} sm={6} display="flex" alignItems="baseline">
                            <Typography
                              component="span"
                              sx={{ color: '#2057A6', fontSize: '20px', paddingRight: '15px' }}>
                              {doc_index + 1}
                            </Typography>
                            {!options && (
                              <Typography
                                component="span"
                                sx={{ fontSize: 14, fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}>
                                {documentName} {required === 'true' ? '*' : ''}
                                <Typography component="span" pl={0.5}>
                                  {infoText && (
                                    <InfoOutlinedIcon
                                      color="info"
                                      fontSize="small"
                                      sx={{
                                        ':hover': {
                                          bgcolor: '#F4FCFC',
                                        },
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => {
                                        setPopUpText({ popUpInfoText: infoText });
                                        handleOpen();
                                      }}
                                    />
                                  )}
                                </Typography>
                              </Typography>
                            )}
                            {options &&
                              documentsList
                                .filter(
                                  (document, index) =>
                                    documentsList.findIndex(
                                      (obj) => document.documentType === obj.documentType
                                    ) === index
                                )
                                .map((doc, index) => (
                                  <React.Fragment key={index}>
                                    <Grid
                                      xs={12}
                                      sm={12}
                                      sx={{ position: 'relative', bottom: '15px' }}>
                                      <MFSelectField
                                        name={`applicants.${ind}.documents.${doc_index}.documentsList.${index}.documentName`}
                                        label={getLabel(documentName, required)}
                                        items={options.map((item: string) => ({
                                          key: item,
                                          value: item,
                                        }))}
                                        applyLabelStyles={true}
                                        disabled={!!application?.isTopup || false}
                                        onChange={({
                                          target: { value },
                                        }: SelectChangeEvent<unknown>) => {
                                          const updatedDocumentList = values.applicants[
                                            ind
                                          ].documents[doc_index].documentsList.map((docList) => {
                                            if (docList.documentType === doc.documentType) {
                                              return {
                                                ...docList,
                                                documentName: value,
                                              };
                                            }
                                            return docList;
                                          });
                                          setFieldValue(
                                            `applicants.${ind}.documents.${doc_index}.documentsList`,
                                            updatedDocumentList
                                          );
                                        }}
                                        infoText={infoText || []}
                                      />
                                    </Grid>
                                  </React.Fragment>
                                ))}
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            display="flex"
                            flexDirection="column"
                            mt={options ? { xs: 1, sm: 3 } : 0}>
                            {documentsList
                              .filter((item) => item.isVisible)
                              .map((doc, index) => (
                                <React.Fragment key={doc.uniqueKey}>
                                  <FileUpload
                                    onFileChange={(
                                      value,
                                      documentId,
                                      errorOnUpload,
                                      removeFile
                                    ) => {
                                      const { file, ...rest } =
                                        values.applicants[ind].documents[doc_index].documentsList[
                                          Number(doc?.uniqueKey?.split('-')[2])
                                        ];
                                      setFieldValue(
                                        `applicants.${ind}.documents.${doc_index}.documentsList.${
                                          doc?.uniqueKey?.split('-')[2]
                                        }`,
                                        {
                                          ...rest,
                                          documentId,
                                          ...(!removeFile && { file }),
                                          errorOnUpload:
                                            errorOnUpload || document_object.errorOnUpload,
                                        }
                                      );
                                    }}
                                    name={`applicants.${ind}.documents.${doc_index}.documentsList.${
                                      doc?.uniqueKey?.split('-')[2]
                                    }.documentId`}
                                    documentData={doc}
                                    disabled={!!application?.isTopup || false}
                                  />
                                  {downloadTemplate && (
                                    <Box sx={{ textAlign: 'center' }}>
                                      <Button
                                        variant="contained"
                                        sx={{
                                          color: 'common.white',
                                          // minWidth: '380px',
                                          width: '100%',
                                          fontWeight: 600,
                                          lineHeight: 1,
                                          fontSize: '12px',
                                          mb: 1,
                                        }}
                                        onClick={async () => {
                                          //running application in local replace "downloadTemplate" with "proxyURL${downloadTemplate}"(EX:-https://aif-uat.kfintech.webileapps.io${downloadTemplate})
                                          window.open(downloadTemplate);
                                        }}>
                                        Download template
                                      </Button>
                                    </Box>
                                  )}
                                  {index > 0 && doc.isVisible && !doc.documentId && (
                                    <DeleteOutlineIcon
                                      onClick={() => {
                                        const getDocs = values.applicants[ind].documents[
                                          doc_index
                                        ].documentsList.map((_item, i) => ({
                                          ..._item,
                                          isVisible:
                                            i !== Number(doc?.uniqueKey?.split('-')[2])
                                              ? _item.isVisible
                                              : false,
                                        }));
                                        // .filter((_, i) => i !== index);
                                        setFieldValue(
                                          `applicants.${ind}.documents.${doc_index}.documentsList`,
                                          getDocs
                                        );
                                      }}
                                      color="error"
                                      sx={{
                                        alignSelf: 'end',
                                        position: 'relative',
                                        bottom: '90px',
                                        left: '22px',
                                        cursor: 'pointer',
                                      }}
                                    />
                                  )}
                                </React.Fragment>
                              ))}
                            {multipleFiles === 'true' &&
                              !application?.isTopup &&
                              documentsList.filter((item) => item.isVisible).length <
                                (documentType === 'additional' ? 3 : 2) && (
                                <Typography
                                  onClick={() => {
                                    const currentDocTypeListLength =
                                      values.applicants[ind].documents[doc_index].documentsList
                                        .length;
                                    const {
                                      required = true,
                                      documentType,
                                      documentName,
                                      // eslint-disable-next-line
                                      documentId,
                                    } = values.applicants[ind].documents[doc_index].documentsList[
                                      currentDocTypeListLength - 1
                                    ];
                                    try {
                                      documentsList
                                        .filter((item) => item.isVisible)
                                        .forEach((item, _i) => {
                                          if (!item.documentId) {
                                            throw Error(
                                              `Please upload the ${getDocNumberForMultipleDocs(
                                                _i + 1
                                              )}document to add more documents`
                                            );
                                          }
                                        });
                                      setFieldValue(
                                        `applicants.${ind}.documents.${doc_index}.documentsList`,
                                        [
                                          ...values.applicants[ind].documents[doc_index]
                                            .documentsList,
                                          {
                                            ...document_object,
                                            uniqueKey:
                                              ind.toString() +
                                              '-' +
                                              doc_index.toString() +
                                              '-' +
                                              currentDocTypeListLength.toString(),
                                            isVisible: true,
                                            documentType,
                                            documentName,
                                            required,
                                          },
                                        ]
                                      );
                                    } catch (e) {
                                      dispatch(showError((e as Error).message));
                                    }
                                  }}
                                  sx={{
                                    color: '#417850',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    textDecoration: 'underline',
                                    alignSelf: 'end',
                                    // position: 'relative',
                                    // bottom:
                                    //   values.applicants[ind].documents[doc_index].documentsList[
                                    //     values.applicants[ind].documents[doc_index].documentsList.length - 1
                                    //   ].documentId !== ''
                                    //     ? '15px'
                                    //     : '50px',

                                    // pointerEvents: applicant.documents[
                                    //   values.applicants[ind].documents.length - 1
                                    // ].documentId
                                    //   ? 'all'
                                    //   : 'none',
                                    cursor: 'pointer',
                                  }}>
                                  {'Add another document'}
                                </Typography>
                              )}
                          </Grid>
                          <Grid item xs={12} sm={6} display="flex" alignItems="baseline"></Grid>
                          <Grid item xs={12} sm={6} display="flex" alignItems="baseline">
                            {documentsList.map((doc, index) => (
                              <React.Fragment key={doc.uniqueKey}>
                                {isBankAccountProof(doc.documentType) &&
                                  isDocTypeCheque(doc.documentName) && (
                                    <DatePicker
                                      label={'Cheque Issuing Date *'}
                                      inputLabelStyles={{
                                        transform: 'unset',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: 'rgba(0,0,0,0.7)',
                                      }}
                                      placeholder={'Enter Cheque Issuing Date'}
                                      name={`applicants.${ind}.documents.${doc_index}.documentsList.${index}.chequeIssuingDate`}
                                    />
                                  )}
                              </React.Fragment>
                            ))}
                          </Grid>
                        </React.Fragment>
                      </>
                    );
                  })}
                  {ENABLE_KRA_DOCUMENT_FETCH && (
                    <KraDocument KraDocument={applicant.kraDocument} num={num + 1} />
                  )}
                </>
              );
            })}
          <ProceedSaveLater
            saveLater={() => {
              setValues({
                ...values,
                saveType: 'save for later',
              });
            }}
            saveAndProceed={() => {
              setValues({
                ...values,
                saveType: 'save and proceed',
              });
            }}
            loader={loading}
            clickedButton={values.saveType}
          />
          <MultipleInfoDialog handleClose={handleClose} open={open} popUpText={popUpText} />
        </Grid>
      )}
    </Formik>
  );
}
